/** Render residency over complete sheet geometry; semantic rows remain in the model. */
import type { RowLocalId } from "../ids/row-local-id.ts";
import type { RowSection } from "./scene.ts";

/** Pixel row windows shared by terminal and document renderers. */
export namespace RowViewport {
  /** All rows, or a body-local window with explicit focused/overlay owner pins. */
  export type Input = {
    /** Retain the complete semantic row sequence. */
    readonly mode: "all";
  } | {
    /** Select every row or crop the body to a pixel window. */
    readonly mode: "window";
    /** Nonnegative top edge of the requested body-local window in pixels. */
    readonly topPx: number;
    /** Nonnegative height of the requested visible window in pixels. */
    readonly heightPx: number;
    /** Additional body pixels retained before the visible window. */
    readonly overscanBeforePx: number;
    /** Additional body pixels retained after the visible window. */
    readonly overscanAfterPx: number;
    /** Focused or overlay-owning row identities retained outside the window. */
    readonly pinned: readonly RowLocalId[];
  };
  /** A row's own paint rectangle, preserving its full semantic position. */
  export interface Row {
    /** Interned row identity used to join residency with the complete model. */
    readonly rowId: RowLocalId;
    /** Original ordinal in the complete semantic row sequence. */
    readonly semanticIndex: number;
    /** Surface rail; header and footer rows remain resident. */
    readonly section: RowSection;
    /** Top edge in body-local pixels; row paint rectangles may begin above zero. */
    readonly topPx: number;
    /** Height of the row's own paint rectangle in pixels. */
    readonly heightPx: number;
  }
  /** A resident row, with the reason its renderer must hold it. */
  export interface Selected {
    /** Complete geometry of the resident row. */
    readonly row: Row;
    /** Why the row remains resident in this selection. */
    readonly reason: "all" | "visible" | "overscan" | "pinned" | "sticky";
  }
  /** Selected rows keep full order and offsets; body extent never shrinks with residency. */
  export interface Selection {
    /** Resident rows in their original semantic order. */
    readonly rows: readonly Selected[];
    /** Number of rows in the complete geometry before cropping. */
    readonly totalRows: number;
    /** Full body scroll extent, unchanged by residency. */
    readonly totalBodyHeightPx: number;
    /** Interval index nodes examined by this selection. */
    readonly geometryNodesVisited: number;
  }
  /** Immutable interval index, rebuilt only when complete row geometry changes. */
  export class Index {
    readonly #rows: readonly Row[];
    readonly #byId = new Map<RowLocalId, Row>();
    readonly #sticky: readonly Row[];
    readonly #tree: IntervalNode | null;
    readonly #bodyHeightPx: number;

    /** Snapshot complete paint geometry and scroll extent; overlapping rectangles are supported. */
    constructor(rows: readonly Row[], totalBodyHeightPx: number) {
      nonnegative(totalBodyHeightPx, "totalBodyHeightPx");
      const positions = new Set<number>();
      this.#rows = Object.freeze(rows.map(row => {
        if (!Number.isFinite(row.topPx)) throw new RangeError("row topPx must be finite");
        nonnegative(row.heightPx, "row heightPx");
        if (!Number.isSafeInteger(row.semanticIndex) || row.semanticIndex < 0 || positions.has(row.semanticIndex)) {
          throw new RangeError("row semanticIndex must be unique and nonnegative");
        }
        if (this.#byId.has(row.rowId)) throw new RangeError("duplicate viewport row identity");
        positions.add(row.semanticIndex);
        const copy = Object.freeze({ ...row });
        this.#byId.set(copy.rowId, copy);
        return copy;
      }).sort((a, b) => a.semanticIndex - b.semanticIndex));
      this.#sticky = this.#rows.filter(row => row.section !== "body");
      const body = this.#rows.filter(row => row.section === "body")
        .sort((a, b) => a.topPx - b.topPx || a.semanticIndex - b.semanticIndex);
      this.#tree = intervalTree(body, 0, body.length);
      this.#bodyHeightPx = totalBodyHeightPx;
    }

    /** Query intersecting own rectangles, sticky surfaces and pins, in full semantic order. */
    select(input: Input): Selection {
      if (input.mode === "all") return Object.freeze({
        rows: Object.freeze(this.#rows.map(row => Object.freeze({ row, reason: "all" as const }))),
        totalRows: this.#rows.length, totalBodyHeightPx: this.#bodyHeightPx, geometryNodesVisited: 0,
      });
      for (const [name, value] of Object.entries({ topPx: input.topPx, heightPx: input.heightPx,
        overscanBeforePx: input.overscanBeforePx, overscanAfterPx: input.overscanAfterPx })) nonnegative(value, name);
      const end = input.topPx + input.heightPx;
      const low = Math.max(0, input.topPx - input.overscanBeforePx);
      const high = end + input.overscanAfterPx;
      if (!Number.isFinite(end) || !Number.isFinite(high)) throw new RangeError("viewport bounds overflow");
      const selected = new Map<RowLocalId, Selected>();
      for (const row of this.#sticky) selected.set(row.rowId, { row, reason: "sticky" });
      let visited = 0;
      const visit = (node: IntervalNode | null): void => {
        if (node == null) return;
        visited++;
        if (node.maxBottom <= low || node.minTop >= high || low === high) return;
        visit(node.left);
        const row = node.row;
        if (row.heightPx > 0 && row.topPx < high && low < row.topPx + row.heightPx) {
          const visible = input.heightPx > 0 && row.topPx < end && input.topPx < row.topPx + row.heightPx;
          selected.set(row.rowId, { row, reason: visible ? "visible" : "overscan" });
        }
        visit(node.right);
      };
      visit(this.#tree);
      for (const id of input.pinned) {
        const row = this.#byId.get(id);
        if (row != null && !selected.has(id)) selected.set(id, { row, reason: "pinned" });
      }
      return Object.freeze({
        rows: Object.freeze([...selected.values()].sort((a, b) => a.row.semanticIndex - b.row.semanticIndex).map(item => Object.freeze(item))),
        totalRows: this.#rows.length, totalBodyHeightPx: this.#bodyHeightPx, geometryNodesVisited: visited,
      });
    }
  }
}

interface IntervalNode {
  /** Complete geometry of the resident row. */
  readonly row: RowViewport.Row;
  readonly left: IntervalNode | null;
  readonly right: IntervalNode | null;
  readonly minTop: number;
  readonly maxBottom: number;
}
function intervalTree(rows: readonly RowViewport.Row[], low: number, high: number): IntervalNode | null {
  if (low === high) return null;
  const middle = Math.floor((low + high) / 2);
  const row = rows[middle];
  if (row == null) throw new RangeError("viewport interval index out of bounds");
  const left = intervalTree(rows, low, middle);
  const right = intervalTree(rows, middle + 1, high);
  return { row, left, right, minTop: Math.min(row.topPx, left?.minTop ?? Infinity, right?.minTop ?? Infinity),
    maxBottom: Math.max(row.topPx + row.heightPx, left?.maxBottom ?? -Infinity, right?.maxBottom ?? -Infinity) };
}
function nonnegative(value: number, name: string): void {
  if (!Number.isFinite(value) || value < 0) throw new RangeError(`${name} must be finite and nonnegative`);
}
